import React, { useState, useCallback } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  InputGroup,
  Spinner,
  Badge,
  FormText,
} from "reactstrap"
import { useHistory } from "react-router-dom"
import DataTable from "react-data-table-component"
import { opcionesPaginacion } from "components/Tables/paginacion"

import Layout from "components/Layout"
import Tooltip from "components/Tooltip/Tooltip"
import { buscarEstablecimientos } from "api/agro"
import toastHandler from "toastHandler"

const BusquedaEstablecimiento = ({ permissionBy }) => {
  const history = useHistory()
  const { setError } = toastHandler()

  const [loading, setLoading] = useState(false)
  const [establecimientos, setEstablecimientos] = useState([])
  const [searchQuery, setSearchQuery] = useState("")
  const [filtroLotes, setFiltroLotes] = useState("")
  const [searched, setSearched] = useState(false)

  const columns = [
    {
      name: "Nombre Establecimiento",
      selector: row => row.nombre,
      sortable: true,
      grow: 2,
    },
    {
      name: "Productor",
      selector: row => row.razonSocial,
      sortable: true,
      grow: 1,
    },
    {
      name: "Provincia",
      selector: row => row.provincia,
      sortable: true,
      width: "140px",
    },
    {
      name: "Localidad",
      selector: row => row.localidad,
      sortable: true,
      width: "140px",
    },
    {
      name: "Sup. Total (ha)",
      selector: row => row.superficieTotalHa,
      sortable: true,
      width: "150px",
      right: true,
      format: row =>
        row.superficieTotalHa
          ? Number(row.superficieTotalHa).toLocaleString("es-AR")
          : "-",
    },
    {
      name: "Lotes",
      selector: row => row.cantidadPoligonos,
      sortable: true,
      width: "100px",
      center: true,
      format: row => row.cantidadPoligonos ?? 0,
    },
    {
      name: "Def. de lotes",
      selector: row => row.tieneLotesDefinidos,
      sortable: true,
      width: "150px",
      center: true,
      cell: row => (
        <Tooltip
          title={
            row.tieneLotesDefinidos
              ? "Tiene lotes dibujados por el operador"
              : "Sin lotes dibujados por el operador (los polígonos aproximados de la importación no cuentan)"
          }
        >
          {/* span enfocable: el tooltip también se abre con teclado */}
          <span tabIndex={0} style={{ display: "inline-block" }}>
            <Badge color={row.tieneLotesDefinidos ? "success" : "secondary"}>
              {row.tieneLotesDefinidos ? "Definido" : "Default"}
            </Badge>
          </span>
        </Tooltip>
      ),
    },
    {
      name: "Acciones",
      cell: row => (
        <Button
          color="primary"
          size="sm"
          title="Ver establecimiento"
          aria-label="Ver establecimiento"
          onClick={() =>
            history.push(
              `/agro/productor/${row.idProductor}?tab=establecimientos&establecimientoId=${row.id}`,
            )
          }
        >
          <i className="mdi mdi-eye" />
        </Button>
      ),
      width: "100px",
      center: true,
    },
  ]

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return

    setLoading(true)
    setSearched(true)

    try {
      const soloLotesDefinidos =
        filtroLotes === "definidos"
          ? true
          : filtroLotes === "default"
            ? false
            : undefined
      const resultado = await buscarEstablecimientos({
        q: searchQuery,
        soloLotesDefinidos,
      })
      setEstablecimientos(resultado || [])
    } catch (err) {
      if (err.response?.status === 404) {
        setEstablecimientos([])
      } else {
        setError(err)
      }
    } finally {
      setLoading(false)
    }
  }, [searchQuery, filtroLotes, setError])

  const handleKeyPress = useCallback(
    e => {
      if (e.key === "Enter") {
        e.preventDefault()
        handleSearch()
      }
    },
    [handleSearch],
  )

  const handleInputChange = useCallback(e => {
    setSearchQuery(e.target.value)
  }, [])

  const breadcrumbItems = [
    { title: "Agropecuario", link: "/agro" },
    { title: "Búsqueda de Establecimiento", link: "#" },
  ]

  return (
    <Layout
      title="Búsqueda de Establecimiento"
      breadcrumbItems={breadcrumbItems}
      permissionBy={permissionBy}
      components={[
        {
          component: (
            <>
              <Row>
                <Col xs={12}>
                  <Card>
                    <CardBody>
                      <Row className="justify-content-center">
                        <Col md={8} lg={6}>
                          <InputGroup>
                            <Input
                              type="text"
                              placeholder="Ingrese nombre del establecimiento..."
                              value={searchQuery}
                              onChange={handleInputChange}
                              onKeyPress={handleKeyPress}
                              maxLength={100}
                              autoFocus
                            />
                            <Button
                              color="primary"
                              onClick={handleSearch}
                              disabled={loading || !searchQuery.trim()}
                            >
                              {loading ? (
                                <Spinner size="sm" />
                              ) : (
                                <i className="bx bx-search" />
                              )}{" "}
                              Buscar
                            </Button>
                          </InputGroup>
                        </Col>
                        <Col md={4} lg={3}>
                          <Input
                            type="select"
                            value={filtroLotes}
                            onChange={e => setFiltroLotes(e.target.value)}
                          >
                            <option value="">Todos los lotes</option>
                            <option value="definidos">
                              Sólo con lotes definidos
                            </option>
                            <option value="default">
                              Sólo con lote default
                            </option>
                          </Input>
                          <FormText data-testid="ayuda-lotes-definidos">
                            Definido = dibujado por el operador; los polígonos
                            aproximados no cuentan.
                          </FormText>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {searched && (
                <Row className="mt-3">
                  <Col xs={12}>
                    <Card>
                      <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h5 className="mb-0">
                            Resultados ({establecimientos.length})
                          </h5>
                        </div>

                        {establecimientos.length > 0 ? (
                          <DataTable
                            key={establecimientos.length}
                            columns={columns}
                            data={establecimientos}
                            pagination
                            paginationPerPage={10}
                            {...opcionesPaginacion(establecimientos.length)}
                            highlightOnHover
                            striped
                            responsive
                            noDataComponent="No se encontraron establecimientos"
                          />
                        ) : (
                          <div className="text-center py-4">
                            <i
                              className="bx bx-search-alt text-muted"
                              style={{ fontSize: "3rem" }}
                            />
                            <p className="text-muted mt-2">
                              No se encontraron establecimientos con los
                              criterios ingresados
                            </p>
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              )}
            </>
          ),
        },
      ]}
    />
  )
}

export default BusquedaEstablecimiento
